/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
import { useContext } from 'react';
import H6 from '@base/H6/H6';
import styled from '@emotion/styled';
import { Spacings, Fonts } from '@styles/index';
import LottieAnimation from '@base/LottieAnimation/LottieAnimation';
import Image from '@base/Image';
import Skeleton from '@material-ui/lab/Skeleton';
import { getImage } from '@utils/imageHelper';
import { getAnimationPath } from '@utils/animationHelpers';
import Dynamic from '@base/Dynamic/Dynamic';
import { GroupsContext } from '@context/GroupsContext';
import GroupsScoreboardCard from '../CommonCards/GroupsScoreboardCard';
import { weekDaysArray } from '../groupsConstants';
import {
  getRemainingTimeString,
  getCurrentWeekString,
  getRemainingHours,
  getDayStatus,
  minUsersToDisplay
} from '../groupsUtils';

const AnimatedScoreboardCard = Dynamic(() =>
  import('./AnimatedScoreboardCard')
);

const lastDayIndex = 6;

const Wrapper = styled.div`
  margin-top: ${Spacings.SPACING_10B};
  margin-bottom: ${Spacings.SPACING_10B};
  position: relative;
`;

const InfoCard = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${Spacings.SPACING_3B} 10px 0 ${Spacings.SPACING_5B};
  border-radius: 10px;
  background-color: var(--color-purple);
  width: 100%;
`;

const Info = styled.div`
  display: flex;
  margin-top: ${Spacings.SPACING_1B};
`;

const AnimationContainer = styled(LottieAnimation)`
  height: ${Spacings.SPACING_10B};
  width: ${Spacings.SPACING_10B};
  margin-right: ${Spacings.SPACING_3B};
  border-radius: 50%;
  background-color: var(--color-i-white);
`;

const TimeStr = styled.span`
  font-size: ${Fonts.EXTRA_SMALL};
  text-transform: uppercase;
  color: var(--color-purple-dark);
  font-weight: ${Fonts.BOLD};
`;

const WeekDaysCard = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--color-base-1);
  border: ${Spacings.SPACING_2} solid var(--color-base-light-0);
  border-radius: ${Spacings.SPACING_15B};
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_3B};
  position: absolute;
  bottom: -16px;
`;

const WeekDayCircle = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 100%;
  background-size: cover;
  width: ${Spacings.SPACING_6B};
  height: ${Spacings.SPACING_6B};
  color: ${({ isCurrentDay, isDayPassed, dayStatus }) =>
    isCurrentDay
      ? dayStatus === 'night'
        ? 'var(--color-i-white)'
        : 'var(--color-i-black)'
      : isDayPassed
      ? 'var(--color-text-tertiary)'
      : 'var(--color-text-secondary)'};
  background-color: ${({ isCurrentDay, isDayPassed }) =>
    isCurrentDay
      ? 'transparent'
      : isDayPassed
      ? 'var(--color-base-fill)'
      : 'var(--color-base-light-0)'};
  background-image: ${({ bgImage, isCurrentDay, isLastDayOfWeek }) =>
    isCurrentDay && !isLastDayOfWeek ? `url(${getImage(bgImage)})` : null};
  border: ${({ isCurrentDay, isLastDayOfWeek }) =>
      isCurrentDay && isLastDayOfWeek ? Spacings.SPACING_1B : '0'}
    solid var(--color-i-blue);
`;

const WeekDay = styled.span`
  font-size: ${Fonts.EXTRA_SMALL};
  font-weight: ${Fonts.BOLD};
`;

const Dash = styled.span`
  height: ${Spacings.SPACING_1};
  width: ${Spacings.SPACING_1B};
  margin-right: ${Spacings.SPACING_6};
  margin-left: ${Spacings.SPACING_6};
  background-color: var(--color-i-black);
`;

const InfoWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  position: relative;
  margin-bottom: ${Spacings.SPACING_7B};
`;

const SkeletonWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DrawerScoreboard = ({
  startDate,
  endDate,
  currentDate,
  goalUID,
  competitionName,
  leaderboard
}) => {
  const groupsContext = useContext(GroupsContext);
  const { users } = groupsContext;
  const timeStr =
    !!leaderboard || getRemainingHours(currentDate, endDate) >= 24
      ? getCurrentWeekString(startDate, endDate)
      : `Ends in ${getRemainingTimeString(currentDate, endDate)}`;

  const currentDay = currentDate.getDay();
  const dayStatus = getDayStatus(currentDate);
  const isShowMoreWrapperVisible = users.length > minUsersToDisplay;

  return (
    <Wrapper>
      <InfoWrapper>
        <InfoCard>
          <Info>
            <AnimationContainer
              src={getAnimationPath('trophy.json')}
              Skeleton={
                <SkeletonWrapper>
                  <Skeleton
                    variant="circle"
                    width={Spacings.SPACING_10B}
                    height={Spacings.SPACING_10B}
                  />
                </SkeletonWrapper>
              }
            />
            <div>
              <H6>{competitionName}</H6>
              <TimeStr>{timeStr}</TimeStr>
            </div>
          </Info>
          <Image
            src="groups/leaderboard.png"
            width={78}
            height={86}
            alt="Scoreboard"
          />
        </InfoCard>
        <WeekDaysCard>
          {weekDaysArray.map((day, index) => {
            const isCurrentDay = index === currentDay;
            const isLastDayOfWeek = currentDay === lastDayIndex;
            const isDayPassed = index < currentDay;

            return (
              <>
                <WeekDayCircle
                  isCurrentDay={isCurrentDay}
                  isLastDayOfWeek={isLastDayOfWeek}
                  isDayPassed={isDayPassed}
                  dayStatus={dayStatus}
                  bgImage={`groups/${dayStatus}.png`}
                >
                  {!(currentDay === lastDayIndex && index === lastDayIndex) && (
                    <WeekDay>{day}</WeekDay>
                  )}
                </WeekDayCircle>
                {index !== weekDaysArray.length - 1 && <Dash />}
              </>
            );
          })}
        </WeekDaysCard>
      </InfoWrapper>
      {isShowMoreWrapperVisible ? (
        <AnimatedScoreboardCard goalUID={goalUID} />
      ) : (
        <GroupsScoreboardCard type="drawer" goalUID={goalUID} />
      )}
    </Wrapper>
  );
};

export default DrawerScoreboard;

DrawerScoreboard.propTypes = {
  endDate: PropTypes.number.isRequired,
  currentDate: PropTypes.number.isRequired,
  goalUID: PropTypes.string.isRequired,
  competitionName: PropTypes.string.isRequired
};
